import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Calculator,
  Users,
  DollarSign,
  TrendingUp,
  Award,
  FileText,
  Filter,
  ChevronDown,
  Upload,
  Download,
  FileSpreadsheet,
  Calendar,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAppData } from '../../core/contexts/AppDataContext';
import { useAttendanceData } from '../../shared/hooks/useAttendanceData';
import { useAvancesRetenuesData } from '../../shared/hooks/useAvancesRetenuesData';
import { useDepartmentData } from '../../shared/hooks/useDepartmentData';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { CustomSelect } from '../../shared/components/custom';
import {
  resolveEmployeeGrossSalary,
  countNationalitySplit,
  formatPayrollDate,
  getPayrollLocale,
  WPS_PAYMENT_DAY,
} from './utils/payrollSaudiFormat';
import { calculerRecapitulatifCotisations } from '../../shared/utils/salaryCalculator';
import SaudiDetailedPayrollTable, {
  buildSaudiPayrollRows,
} from './components/salaries/SaudiDetailedPayrollTable';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildSalairesImportExportProps,
  mapSalairesStatCards,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import { useSalairesConfig } from './useSalairesConfig';

const ALL_DEPARTMENTS = '__all__';

const CalculSalaires = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { settings } = useAppData();
  const salariesConfig = useSalairesConfig();
  const payrollSettings = useMemo(
    () => ({ ...settings, salaries: { ...salariesConfig, ...(settings?.salaries || {}) } }),
    [settings, salariesConfig],
  );
  const { employees: hrEmployees, loading: employeesLoading } = useSalairesEmployeeOptions();
  const employees = useMemo(
    () => hrEmployees.map((emp) => ({
      ...emp,
      mat: emp.mat || emp.employeeId || String(emp.id),
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      affectation: emp.affectation || emp.department || emp.departement || '',
      salaireBase: emp.salaireBase ?? emp.salary ?? emp.basic_salary ?? 0,
    })),
    [hrEmployees],
  );
  const { departments, loading: departmentsLoading } = useDepartmentData();
  const { avancesRetenuesData: avancesRetenues, loading: avancesRetenuesLoading } =
    useAvancesRetenuesData();
  const payrollLocale = getPayrollLocale();
  const payrollYear = new Date().getFullYear();
  const periodChoices = [
    { value: `${payrollYear}-01`, monthKey: 'month_january', year: payrollYear, month: 1 },
    { value: `${payrollYear}-02`, monthKey: 'month_february', year: payrollYear, month: 2 },
    { value: `${payrollYear}-03`, monthKey: 'month_march', year: payrollYear, month: 3 },
    { value: `${payrollYear}-04`, monthKey: 'month_april', year: payrollYear, month: 4 },
    { value: `${payrollYear}-05`, monthKey: 'month_may', year: payrollYear, month: 5 },
    { value: `${payrollYear}-06`, monthKey: 'month_june', year: payrollYear, month: 6 },
    { value: `${payrollYear}-07`, monthKey: 'month_july', year: payrollYear, month: 7 },
    { value: `${payrollYear}-08`, monthKey: 'month_august', year: payrollYear, month: 8 },
    { value: `${payrollYear}-09`, monthKey: 'month_september', year: payrollYear, month: 9 },
    { value: `${payrollYear}-10`, monthKey: 'month_october', year: payrollYear, month: 10 },
    { value: `${payrollYear}-11`, monthKey: 'month_november', year: payrollYear, month: 11 },
    { value: `${payrollYear}-12`, monthKey: 'month_december', year: payrollYear, month: 12 },
  ];
  const defaultPeriod = `${payrollYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [mois, setMois] = useState(defaultPeriod);
  const [departement, setDepartement] = useState(ALL_DEPARTMENTS);
  const [isImportDropdownOpen, setIsImportDropdownOpen] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const currentPeriod = periodChoices.find((c) => c.value === mois) || periodChoices[periodChoices.length - 1];
  const currentMonthNum = currentPeriod.month;
  const currentYear = currentPeriod.year;
  const { attendanceData: pointageData, loading: pointageLoading } = useAttendanceData(
    currentMonthNum,
    currentYear
  );

  const formatPeriod = (value) => {
    const choice = periodChoices.find((c) => c.value === value);
    if (choice) {
      const start = new Date(choice.year, choice.month - 1, 1);
      const end = new Date(choice.year, choice.month, 0);
      return p('period_range', {
        start: formatPayrollDate(start, { language: payrollLocale }),
        end: formatPayrollDate(end, { language: payrollLocale }),
        wpsDay: WPS_PAYMENT_DAY,
      });
    }
    return value || '';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target))
        setIsImportDropdownOpen(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target))
        setIsExportDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = useMemo(() => {
    let list =
      departement === ALL_DEPARTMENTS
        ? employees
        : employees.filter((emp) => emp.affectation === departement);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (emp) =>
          String(emp.mat || emp.matricule || '').toLowerCase().includes(q)
          || String(emp.nom || '').toLowerCase().includes(q)
          || String(emp.prenom || '').toLowerCase().includes(q)
          || String(emp.affectation || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [employees, departement, searchTerm]);

  const payrollRows = useMemo(
    () =>
      buildSaudiPayrollRows(filteredEmployees, {
        settings: payrollSettings,
        pointageData,
        avancesRetenues,
      }),
    [filteredEmployees, payrollSettings, pointageData, avancesRetenues]
  );

  const exportToExcel = () => {
    const sheetData = payrollRows.map((row) => {
      const emp = row.employee;
      return {
        [p('col_num')]: row.index,
        [p('col_mat')]: emp.mat,
        [p('col_nom')]: emp.nom,
        [p('col_prenom')]: emp.prenom,
        [p('col_assignment')]: emp.affectation,
        [p('field_nationality')]: row.saudi ? p('nationality_saudi') : p('nationality_expat'),
        [p('col_iban')]: emp.iban || emp.numCcp || '',
        [p('col_basic_salary')]: row.basic,
        [p('col_housing_allowance')]: row.allowances.housing,
        [p('col_transport')]: row.allowances.transport,
        [p('col_bonuses')]: row.bonuses,
        [p('col_overtime_hours')]: row.overtimePay,
        [p('col_gross_salary')]: row.gross,
        [p('col_days_worked')]: row.attendance?.nbrJoursTravailles ?? '',
        [p('col_gosi_base')]: row.gosiBase,
        [p('col_gosi_employee')]: row.gosiEmployee,
        [p('col_gosi_employer')]: row.gosiEmployer,
        [p('col_saned_employee')]: row.sanedEmployee,
        [p('col_saned_employer')]: row.sanedEmployer,
        [p('col_eosb_provision')]: row.eosb,
        [p('col_advances_deductions')]: row.otherDeductions,
        [p('col_total_deductions')]: row.totalDeductions,
        [p('col_net_pay')]: row.netPay,
      };
    });
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, p('export_sheet_name'));
    XLSX.writeFile(wb, `payroll-${mois}.xlsx`);
  };

  if (employeesLoading || departmentsLoading || pointageLoading || avancesRetenuesLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white text-lg">{p('loading_data')}</div>
      </div>
    );
  }

  const nationalitySplit = countNationalitySplit(filteredEmployees);
  const salaryStats = {
    totalEmployees: filteredEmployees.length,
    saudiEmployees: nationalitySplit.saudi,
    expatEmployees: nationalitySplit.expat,
    totalPayroll: filteredEmployees.reduce((sum, emp) => {
      const recap = calculerRecapitulatifCotisations(
        emp,
        resolveEmployeeGrossSalary(emp, payrollSettings),
        payrollSettings
      );
      return sum + (recap.netComptable || emp.salaireNetMensuel || 0);
    }, 0),
    avgSalary:
      filteredEmployees.length > 0
        ? Math.round(
            filteredEmployees.reduce((sum, emp) => {
              const recap = calculerRecapitulatifCotisations(
                emp,
                resolveEmployeeGrossSalary(emp, payrollSettings),
                payrollSettings
              );
              return sum + (recap.netComptable || emp.salaireNetMensuel || 0);
            }, 0) / filteredEmployees.length
          )
        : 0,
    totalGosiEmployee: filteredEmployees.reduce((sum, emp) => {
      const recap = calculerRecapitulatifCotisations(
        emp,
        resolveEmployeeGrossSalary(emp, payrollSettings),
        payrollSettings
      );
      return sum + (recap.retenueGOSISalarie ?? recap.retenueSSSalarie ?? 0) + (recap.retenueSANEDSalarie ?? 0);
    }, 0),
  };

  const departmentOptions = [
    {
      value: ALL_DEPARTMENTS,
      label: p('filter_all_departments'),
      icon: <Filter className="h-4 w-4 text-white/70" />,
    },
    ...(departments || [])
      .filter((dept) => dept.status === 'active')
      .map((dept) => ({ value: dept.name, label: dept.name })),
  ];

  const listStats = mapSalairesStatCards([
    { key: 'employees', label: p('stat_total_employees'), value: salaryStats.totalEmployees, icon: Users },
    { key: 'payroll', label: p('stat_payroll_mass'), value: formatBrandMoney(salaryStats.totalPayroll), icon: DollarSign },
    { key: 'avg', label: p('stat_avg_salary'), value: formatBrandMoney(salaryStats.avgSalary), icon: TrendingUp },
    { key: 'gosi', label: p('stat_gosi_deductions'), value: formatBrandMoney(salaryStats.totalGosiEmployee), icon: Award },
  ]);

  const importExportProps = buildSalairesImportExportProps({
    showImportDropdown: isImportDropdownOpen,
    setShowImportDropdown: setIsImportDropdownOpen,
    showExportDropdown: isExportDropdownOpen,
    setShowExportDropdown: setIsExportDropdownOpen,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
    importLabel: p('import_label'),
    exportLabel: p('export_label'),
    handlers: {
      onExportExcel: exportToExcel,
    },
  });

  const departmentFilterContent = (
    <div className="p-4 border-b border-white/10 grid grid-cols-1 md:grid-cols-3 gap-4">
      <CustomSelect
        value={mois}
        onChange={(newValue) => setMois(newValue)}
        options={periodChoices.map((choice) => ({
          value: choice.value,
          label: `${p(choice.monthKey)} ${choice.year}`,
        }))}
        placeholder={p('period_prefix', { period: formatPeriod(mois) })}
        autoSelect={false}
        searchable
      />
      <CustomSelect
        value={departement}
        onChange={(newValue) => setDepartement(newValue)}
        options={departmentOptions}
        placeholder={p('filter_all_departments')}
        autoSelect={false}
        searchable
        creatable
      />
    </div>
  );

  return (
    <FacturationListPageLayout
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title_detailed_calc')}
      subtitle={p('period_prefix', { period: formatPeriod(mois) })}
      icon={Calculator}
      stats={listStats}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
      importExport={importExportProps}
    >
      {departmentFilterContent}
      <p className="px-4 pt-2 pb-2 text-white/50 text-sm">{p('saudi_table_subtitle')}</p>
      <div className={facturationTableScrollClass(qawaem)}>
        <div className="p-2 pt-0">
          <SaudiDetailedPayrollTable
            employees={filteredEmployees}
            settings={payrollSettings}
            pointageData={pointageData}
            avancesRetenues={avancesRetenues}
          />
        </div>
      </div>
    </FacturationListPageLayout>
  );
};

export default CalculSalaires;
